import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Users, 
  Briefcase, 
  Calendar, 
  TrendingUp, 
  DollarSign, 
  Activity,
  LogOut,
  Trash2,
  BarChart3,
  Building2,
  UserCheck,
  GraduationCap,
  Heart,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Shield,
  MapPin,
  Mail,
  MessageSquare,
  ShoppingBag,
  Sparkles,
  FileText,
  ClipboardCheck,
  ExternalLink,
  Compass,
  UserPlus,
  User,
  ShieldAlert,
  ShieldCheck,
  RotateCcw,
  AlertTriangle,
  Video,
  Radio
} from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';

export default function AdminDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [mentorApplications, setMentorApplications] = useState([]);
  const [mentorAppStats, setMentorAppStats] = useState(null);
  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [bgCheckStats, setBgCheckStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedBgCheck, setSelectedBgCheck] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processingApp, setProcessingApp] = useState(false);
  const [processingBgCheck, setProcessingBgCheck] = useState(false);
  const [moderationCases, setModerationCases] = useState([]);
  const [moderationStats, setModerationStats] = useState(null);
  const [moderationNotes, setModerationNotes] = useState({});
  const [safetyLoading, setSafetyLoading] = useState(true);
  const [safetyError, setSafetyError] = useState('');
  const [adminBookings, setAdminBookings] = useState([]);
  const navigate = useNavigate();

  const adminLaunchpad = [
    {
      group: 'Control Rooms',
      items: [
        { label: 'Company Dashboard', path: '/company/dashboard', icon: Building2, note: 'Post jobs, add slots, boost listings' },
        { label: 'Candidate Dashboard', path: '/candidate/dashboard', icon: UserCheck, note: 'Browse jobs and book interviews' },
        { label: 'Candidate Profiles', path: '/browse-candidates', icon: Users, note: 'Search public candidate profiles' },
        { label: 'My Profile', path: '/profile', icon: User, note: 'Open the signed-in profile page' }
      ]
    },
    {
      group: 'Revenue Pages',
      items: [
        { label: 'Marketplace', path: '/marketplace', icon: ShoppingBag, note: 'Paid tools, referrals, mentors' },
        { label: 'Background Checks', path: '/background-check', icon: Shield, note: 'Candidate verification flow' },
        { label: 'Mentorship', path: '/mentorship', icon: GraduationCap, note: 'Book mentors and sessions' }
      ]
    },
    {
      group: 'Candidate Tools',
      items: [
        { label: 'Resume Builder', path: '/resume-builder', icon: FileText, note: 'Resume creation and export' },
        { label: 'Interview Prep', path: '/interview-prep', icon: ClipboardCheck, note: 'Practice plans and coaching' },
        { label: 'Community Chat', path: '/community', icon: MessageSquare, note: 'Rooms, job filters, mentor chat' },
        { label: 'Become a Mentor', path: '/become-mentor', icon: UserPlus, note: 'Mentor application funnel' }
      ]
    }
  ];

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Access denied: Admin only');
      navigate('/');
      return;
    }
    loadStats();
    loadUsers();
    loadJobs();
    loadAnalytics();
    loadMentorApplications();
    loadBackgroundChecks();
    loadModerationCases();
    loadAdminBookings();
  }, [user]);

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load statistics');
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const res = await api.get('/admin/analytics');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to load analytics');
    }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const loadJobs = async () => {
    try {
      const res = await api.get('/admin/jobs');
      setJobs(res.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    }
  };

  const loadMentorApplications = async () => {
    try {
      const [appsRes, statsRes] = await Promise.all([
        api.get('/mentor-applications'),
        api.get('/admin/mentor-applications/stats')
      ]);
      setMentorApplications(appsRes.data);
      setMentorAppStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load mentor applications');
    }
  };

  const loadBackgroundChecks = async () => {
    try {
      const [checksRes, statsRes] = await Promise.all([
        api.get('/admin/background-checks'),
        api.get('/admin/background-checks/stats')
      ]);
      setBackgroundChecks(checksRes.data);
      setBgCheckStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load background checks');
    }
  };

  const loadModerationCases = async () => {
    setSafetyLoading(true);
    setSafetyError('');
    try {
      const [casesRes, statsRes] = await Promise.all([
        api.get('/admin/moderation/cases?case_status=pending'),
        api.get('/admin/moderation/stats'),
      ]);
      setModerationCases(casesRes.data);
      setModerationStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load safety review queue');
      setSafetyError(error.response?.data?.detail || 'The safety review queue could not be loaded.');
    } finally {
      setSafetyLoading(false);
    }
  };

  const loadAdminBookings = async () => {
    try {
      const res = await api.get('/admin/bookings');
      setAdminBookings(res.data);
    } catch (error) {
      console.error('Failed to load admin interviews');
    }
  };

  const openInterviewRoom = async (bookingId) => {
    try {
      await api.post(`/video/room?booking_id=${bookingId}`);
      navigate(`/video/${bookingId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not open the video room');
    }
  };

  const reviewModerationCase = async (caseId, action) => {
    try {
      await api.put(`/admin/moderation/cases/${caseId}/review`, {
        action,
        notes: moderationNotes[caseId] || '',
        hold_hours: 24,
      });
      toast.success(action === 'restore' || action === 'dismiss' ? 'Account restored and case closed' : action === 'warn' ? 'Warning recorded and case closed' : 'Hold extended for 24 hours');
      loadModerationCases();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not review safety case');
    }
  };

  const handleApproveApplication = async (applicationId) => {
    setProcessingApp(true);
    try {
      await api.put(`/mentor-applications/${applicationId}/approve`);
      toast.success('Application approved! Mentor profile created.');
      loadMentorApplications();
      setSelectedApplication(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setProcessingApp(false);
    }
  };

  const handleRejectApplication = async (applicationId) => {
    setProcessingApp(true);
    try {
      await api.put(`/mentor-applications/${applicationId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Application rejected');
      loadMentorApplications();
      setSelectedApplication(null);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    } finally {
      setProcessingApp(false);
    }
  };

  const handleMarkBgCheckProcessing = async (checkId) => {
    setProcessingBgCheck(true);
    try {
      await api.put(`/admin/background-checks/${checkId}/process`);
      toast.success('Background check marked as processing');
      loadBackgroundChecks();
      setSelectedBgCheck(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update background check');
    } finally {
      setProcessingBgCheck(false);
    }
  };

  const handleMarkBgCheckComplete = async (checkId) => {
    setProcessingBgCheck(true);
    try {
      await api.put(`/admin/background-checks/${checkId}/complete`);
      toast.success('Background check marked as completed');
      loadBackgroundChecks();
      setSelectedBgCheck(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete background check');
    } finally {
      setProcessingBgCheck(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      loadUsers();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      await api.delete(`/admin/jobs/${jobId}`);
      toast.success('Job deleted');
      loadJobs();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-slate-900 text-white sticky top-0 z-50 border-b border-slate-800">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="The Interview Logo" className="w-10 h-10" />
            <div>
              <h1 className="text-2xl font-heading font-bold">Admin Dashboard</h1>
              <p className="text-sm text-gray-400">Platform Monitoring & Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => setActiveTab('launchpad')} className="border-slate-700" data-testid="admin-launchpad-btn">
              <Compass size={16} className="mr-2" />
              All Pages
            </Button>
            <span className="text-sm">{user?.full_name}</span>
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-slate-700" data-testid="admin-logout-btn">
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('launchpad')}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'launchpad' ? 'border-b-2 border-cyan-600 text-cyan-700' : 'text-gray-600'}`}
            data-testid="tab-launchpad"
          >
            <Compass size={16} className="inline mr-1" />
            Page Launchpad
          </button>
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'overview' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'analytics' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
            data-testid="tab-analytics"
          >
            <BarChart3 size={16} className="inline mr-1" />
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'users' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
            data-testid="tab-users"
          >
            Users ({stats?.total_users})
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'jobs' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
            data-testid="tab-jobs"
          >
            Jobs ({stats?.total_jobs})
          </button>
          <button
            onClick={() => setActiveTab('safety')}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'safety' ? 'border-b-2 border-red-600 text-red-700' : 'text-gray-600'}`}
            data-testid="tab-safety"
          >
            <ShieldAlert size={16} className="inline mr-1" />
            Safety Review {moderationStats?.pending > 0 && <span className="ml-1 bg-red-600 px-2 py-0.5 text-xs text-white">{moderationStats.pending}</span>}
          </button>
          <button
            onClick={() => setActiveTab('interviews')}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'interviews' ? 'border-b-2 border-emerald-600 text-emerald-700' : 'text-gray-600'}`}
            data-testid="tab-interviews"
          >
            <Video size={16} className="inline mr-1" />
            Live Interviews ({adminBookings.length})
          </button>
          <button
            onClick={() => setActiveTab('mentor-applications')}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'mentor-applications' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-600'}`}
            data-testid="tab-mentor-applications"
          >
            <Heart size={16} className="inline mr-1" />
            Mentor Apps {mentorAppStats?.pending > 0 && (
              <span className="ml-1 bg-pink-500 text-white text-xs px-2 py-0.5 rounded-full">
                {mentorAppStats.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('background-checks')}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'background-checks' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
            data-testid="tab-background-checks"
          >
            <Shield size={16} className="inline mr-1" />
            BG Checks {bgCheckStats?.pending_review > 0 && (
              <span className="ml-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                {bgCheckStats.pending_review}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'launchpad' && (
          <div className="space-y-8">
            <div className="bg-slate-950 text-white p-6 border border-slate-800">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-cyan-300 font-medium mb-2">
                    <Sparkles size={18} />
                    Admin operator mode
                  </div>
                  <h2 className="text-3xl font-heading font-bold">See the platform like every user does.</h2>
                  <p className="text-slate-300 mt-2 max-w-3xl">
                    Admin can now open candidate, company, marketplace, mentor, chat, and paid-tool pages from one command center.
                  </p>
                </div>
                <Badge className="bg-cyan-500 text-slate-950">All access</Badge>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {adminLaunchpad.map((section) => (
                <Card key={section.group} className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">{section.group}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          className="w-full text-left border p-4 hover:border-cyan-500 hover:bg-cyan-50 transition-colors"
                          data-testid={`admin-open-${item.path.replaceAll('/', '-').slice(1)}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center">
                              <Icon size={18} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between gap-2 font-semibold">
                                {item.label}
                                <ExternalLink size={14} className="text-muted-foreground" />
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">{item.note}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 border" data-testid="stat-total-users">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
                    <Users className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats.total_users}</div>
                    <div className="text-sm text-muted-foreground">Total Users</div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 border" data-testid="stat-companies">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
                    <Briefcase className="text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats.total_companies}</div>
                    <div className="text-sm text-muted-foreground">Companies</div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 border" data-testid="stat-candidates">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded flex items-center justify-center">
                    <Users className="text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats.total_candidates}</div>
                    <div className="text-sm text-muted-foreground">Candidates</div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 border" data-testid="stat-jobs">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded flex items-center justify-center">
                    <Briefcase className="text-amber-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats.total_jobs}</div>
                    <div className="text-sm text-muted-foreground">Job Listings</div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 border" data-testid="stat-bookings">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded flex items-center justify-center">
                    <Calendar className="text-indigo-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats.total_bookings}</div>
                    <div className="text-sm text-muted-foreground">Interviews Booked</div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 border" data-testid="stat-boosted">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded flex items-center justify-center">
                    <TrendingUp className="text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stats.total_boosted_jobs}</div>
                    <div className="text-sm text-muted-foreground">Boosted Jobs</div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 border col-span-2" data-testid="stat-revenue">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded flex items-center justify-center">
                    <DollarSign className="text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">${stats.revenue_stats.total_boost_revenue}</div>
                    <div className="text-sm text-muted-foreground">Estimated Boost Revenue</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 border">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Activity size={20} />
                  Recent Users
                </h2>
                <div className="space-y-3" data-testid="recent-users-list">
                  {stats.recent_users.slice(0, 5).map((u) => (
                    <div key={u.id} className="flex justify-between items-center border-b pb-2">
                      <div>
                        <p className="font-medium">{u.full_name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        u.role === 'company' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {u.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 border">
                <h2 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Calendar size={20} />
                  Recent Bookings
                </h2>
                <div className="space-y-3" data-testid="recent-bookings-list">
                  {stats.recent_bookings.slice(0, 5).map((booking) => (
                    <div key={booking.id} className="border-b pb-2">
                      <p className="font-medium">Booking ID: {booking.id.slice(0, 8)}...</p>
                      <p className="text-sm text-muted-foreground">
                        Status: <span className="text-blue-600">{booking.status}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white border">
            <div className="p-6">
              <h2 className="text-xl font-heading font-semibold mb-4">All Users</h2>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Role</th>
                      <th className="text-left p-3">Company</th>
                      <th className="text-left p-3">Safety</th>
                      <th className="text-left p-3">Created</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b hover:bg-gray-50">
                        <td className="p-3">{u.full_name}</td>
                        <td className="p-3">{u.email}</td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-1 rounded ${
                            u.role === 'admin' ? 'bg-red-100 text-red-800' :
                            u.role === 'company' ? 'bg-blue-100 text-blue-800' : 
                            'bg-green-100 text-green-800'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="p-3">{u.company_name || '-'}</td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-1 ${u.moderation_status === 'on_hold' ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                            {u.moderation_status === 'on_hold' ? 'On hold' : 'Active'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-right">
                          {u.role !== 'admin' && (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDeleteUser(u.id)}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'interviews' && (
          <div className="space-y-6" data-testid="admin-video-control-center">
            <div className="flex flex-wrap items-center justify-between gap-4 border bg-slate-950 p-5 text-white">
              <div>
                <p className="flex items-center gap-2 text-sm font-medium text-emerald-300"><Radio size={16} /> Native video service online</p>
                <h2 className="mt-1 text-2xl font-heading font-bold">Interview Control Center</h2>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="border-slate-600 bg-slate-900 text-white" onClick={loadAdminBookings}>Refresh</Button>
                <Button className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" onClick={() => openInterviewRoom(`admin-test-${user.id}`)} data-testid="open-video-diagnostic">
                  <Video size={16} className="mr-2" /> Test Camera & Tools
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto border bg-white">
              <table className="w-full min-w-[900px]" data-testid="admin-interviews-table">
                <thead className="bg-slate-50">
                  <tr className="border-b">
                    <th className="p-3 text-left">Interview</th>
                    <th className="p-3 text-left">Candidate</th>
                    <th className="p-3 text-left">Company</th>
                    <th className="p-3 text-left">Scheduled</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-right">Room</th>
                  </tr>
                </thead>
                <tbody>
                  {adminBookings.map((booking) => (
                    <tr key={booking.id} className="border-b align-top hover:bg-slate-50">
                      <td className="p-3"><p className="font-medium">{booking.job?.title || 'Interview'}</p><p className="mt-1 text-xs text-muted-foreground">{booking.id.slice(0, 12)}</p></td>
                      <td className="p-3"><p>{booking.candidate?.full_name || booking.candidate_name || 'Candidate unavailable'}</p><p className="text-xs text-muted-foreground">{booking.candidate?.email || ''}</p></td>
                      <td className="p-3"><p>{booking.job?.company_name || booking.company?.company_name || booking.company?.full_name || 'Company unavailable'}</p></td>
                      <td className="p-3 text-sm">{booking.slot?.start_time ? new Date(booking.slot.start_time).toLocaleString() : 'Time not attached'}</td>
                      <td className="p-3"><Badge className={booking.status === 'completed' ? 'bg-emerald-600' : booking.status === 'cancelled' ? 'bg-red-600' : 'bg-blue-600'}>{booking.status || 'scheduled'}</Badge></td>
                      <td className="p-3 text-right"><Button size="sm" onClick={() => openInterviewRoom(booking.id)} data-testid={`admin-join-interview-${booking.id}`}><Video size={14} className="mr-2" /> Open Room</Button></td>
                    </tr>
                  ))}
                  {adminBookings.length === 0 && (
                    <tr><td colSpan="6" className="p-12 text-center text-muted-foreground">No candidate interviews are scheduled yet. The diagnostic studio is available above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'safety' && (
          <div className="space-y-6" data-testid="safety-review-queue">
            {safetyLoading && (
              <div className="border bg-white p-5 text-sm text-muted-foreground">Loading safety review queue...</div>
            )}

            {safetyError && (
              <div className="flex flex-wrap items-center justify-between gap-3 border border-red-200 bg-red-50 p-4 text-sm text-red-900" role="alert">
                <span>{safetyError}</span>
                <Button size="sm" variant="outline" onClick={loadModerationCases}>Try Again</Button>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-4">
              <div className="border bg-white p-4"><p className="text-sm text-muted-foreground">Pending review</p><p className="mt-1 text-3xl font-bold text-red-700">{moderationStats?.pending || 0}</p></div>
              <div className="border bg-white p-4"><p className="text-sm text-muted-foreground">Accounts on hold</p><p className="mt-1 text-3xl font-bold text-amber-700">{moderationStats?.on_hold || 0}</p></div>
              <div className="border bg-white p-4"><p className="text-sm text-muted-foreground">Automated flags</p><p className="mt-1 text-3xl font-bold">{moderationStats?.automated_flags || 0}</p></div>
              <div className="border bg-white p-4"><p className="text-sm text-muted-foreground">User reports</p><p className="mt-1 text-3xl font-bold">{moderationStats?.user_reports || 0}</p></div>
            </div>

            <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
              <p className="flex items-center gap-2 font-semibold"><AlertTriangle size={17} /> Human review controls every final decision</p>
              <p className="mt-1">AI can block content and place a temporary hold, but it does not make hiring decisions. Review context, document the reason, then restore, warn, dismiss, or extend the hold.</p>
            </div>

            {moderationCases.length === 0 ? (
              <div className="border bg-white p-12 text-center"><ShieldCheck className="mx-auto text-emerald-600" size={36} /><p className="mt-3 font-semibold">Safety queue is clear</p><p className="mt-1 text-sm text-muted-foreground">New AI flags and confidential reports will appear here.</p></div>
            ) : moderationCases.map((item) => (
              <div key={item.id} className="border bg-white p-5" data-testid={`moderation-case-${item.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold">{item.subject_name}</h3>
                      <Badge variant="outline">{item.origin === 'automated' ? 'Automated flag' : 'User report'}</Badge>
                      <Badge className={item.severity === 'severe' ? 'bg-red-700' : 'bg-amber-600'}>{item.severity}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{item.source_type?.replaceAll('_', ' ')} • {new Date(item.created_at).toLocaleString()}</p>
                  </div>
                  <span className="text-xs font-medium uppercase text-red-700">{item.action?.replaceAll('_', ' ')}</span>
                </div>
                <div className="mt-4 border bg-slate-50 p-3 text-sm">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Flagged context</p>
                  <p className="mt-1 whitespace-pre-wrap break-words">{item.content_excerpt || 'No additional details were submitted.'}</p>
                  <div className="mt-2 flex flex-wrap gap-2">{(item.categories || []).map((category) => <span key={category} className="bg-red-100 px-2 py-1 text-xs text-red-800">{category}</span>)}</div>
                </div>
                <Textarea className="mt-4" rows={2} value={moderationNotes[item.id] || ''} onChange={(event) => setModerationNotes((current) => ({ ...current, [item.id]: event.target.value }))} placeholder="Required review notes or context..." />
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => reviewModerationCase(item.id, 'restore')}><RotateCcw size={14} className="mr-2" /> Restore Account</Button>
                  <Button size="sm" variant="outline" onClick={() => reviewModerationCase(item.id, 'warn')}>Warn & Restore</Button>
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-800" onClick={() => reviewModerationCase(item.id, 'keep_hold')}>Keep Hold 24h</Button>
                  <Button size="sm" variant="ghost" onClick={() => reviewModerationCase(item.id, 'dismiss')}>Dismiss Report</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div className="bg-white border">
            <div className="p-6">
              <h2 className="text-xl font-heading font-semibold mb-4">All Job Listings</h2>
              <div className="space-y-4" data-testid="jobs-list">
                {jobs.map((job) => (
                  <div key={job.id} className="border p-4 flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{job.title}</h3>
                        {job.is_boosted && (
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                            🚀 Boosted
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{job.company_name}</p>
                      <p className="text-sm">{job.location}</p>
                      <p className="text-sm text-muted-foreground mt-2">{job.description}</p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDeleteJob(job.id)}
                      data-testid={`delete-job-${job.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-8">
            {/* Revenue Breakdown */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card data-testid="revenue-marketplace">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Marketplace Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">${analytics.revenue_breakdown.marketplace.toFixed(2)}</div>
                </CardContent>
              </Card>
              
              <Card data-testid="revenue-boosts">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Job Boost Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">${analytics.revenue_breakdown.job_boosts.toFixed(2)}</div>
                </CardContent>
              </Card>
              
              <Card data-testid="revenue-mentorship">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Mentorship Fees (20%)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-purple-600">${analytics.revenue_breakdown.mentorship_fees.toFixed(2)}</div>
                </CardContent>
              </Card>
              
              <Card data-testid="revenue-bgchecks">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Background Checks</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-600">${analytics.revenue_breakdown.background_checks.toFixed(2)}</div>
                </CardContent>
              </Card>
            </div>

            {/* Total Revenue Banner */}
            <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Total Platform Revenue</p>
                    <p className="text-4xl font-bold">${analytics.revenue_breakdown.total.toFixed(2)}</p>
                  </div>
                  <DollarSign size={48} className="text-green-200" />
                </div>
              </CardContent>
            </Card>

            {/* Interview Stats & Mentor Stats */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar size={20} />
                    Interview Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Total Scheduled</span>
                      <span className="font-bold text-lg">{analytics.interview_stats.total_scheduled}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Completed</span>
                      <span className="font-bold text-lg text-green-600">{analytics.interview_stats.completed}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Completion Rate</span>
                      <span className="font-bold text-lg">{analytics.interview_stats.completion_rate}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${analytics.interview_stats.completion_rate}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap size={20} />
                    Mentor Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Total Mentors</span>
                      <span className="font-bold text-lg">{analytics.mentor_stats.total_mentors}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Total Sessions</span>
                      <span className="font-bold text-lg text-purple-600">{analytics.mentor_stats.total_sessions}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Weekly Active Users</span>
                      <span className="font-bold text-lg text-blue-600">{analytics.weekly_active_users}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Companies */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 size={20} />
                  Top Companies by Bookings
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.top_companies.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.top_companies.map((company, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                            {idx + 1}
                          </span>
                          <span className="font-medium">{company.name}</span>
                        </div>
                        <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                          {company.bookings} bookings
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No booking data yet</p>
                )}
              </CardContent>
            </Card>

            {/* User Growth Chart (Simple) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp size={20} />
                  User Growth (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-40 flex items-end gap-1">
                  {analytics.user_growth.slice(-30).map((day, idx) => (
                    <div 
                      key={idx}
                      className="flex-1 bg-blue-500 rounded-t hover:bg-blue-600 transition-colors"
                      style={{ 
                        height: `${Math.max(4, day.count * 20)}px`,
                        minHeight: '4px'
                      }}
                      title={`${day.date}: ${day.count} new users`}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>30 days ago</span>
                  <span>Today</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Mentor Applications Tab */}
        {activeTab === 'mentor-applications' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {mentorAppStats && (
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Clock className="text-yellow-500" size={24} />
                      <div>
                        <p className="text-2xl font-bold">{mentorAppStats.pending}</p>
                        <p className="text-sm text-muted-foreground">Pending Review</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-green-500" size={24} />
                      <div>
                        <p className="text-2xl font-bold">{mentorAppStats.approved}</p>
                        <p className="text-sm text-muted-foreground">Approved</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <XCircle className="text-red-500" size={24} />
                      <div>
                        <p className="text-2xl font-bold">{mentorAppStats.rejected}</p>
                        <p className="text-sm text-muted-foreground">Rejected</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Heart className="text-pink-500" size={24} />
                      <div>
                        <p className="text-2xl font-bold">{mentorAppStats.total}</p>
                        <p className="text-sm text-muted-foreground">Total Applications</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Applications List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart size={20} />
                  Mentor Applications
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mentorApplications.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No mentor applications yet</p>
                ) : (
                  <div className="space-y-4">
                    {mentorApplications.map((app) => (
                      <div 
                        key={app.id} 
                        className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        data-testid={`mentor-app-${app.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{app.user_name}</h3>
                              <Badge className={
                                app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                app.status === 'approved' ? 'bg-green-100 text-green-800' :
                                'bg-red-100 text-red-800'
                              }>
                                {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{app.user_email}</p>
                            <p className="text-sm mt-1">
                              <span className="font-medium">{app.current_role}</span> at {app.company}
                            </p>
                            <div className="flex flex-wrap gap-4 mt-2 text-sm">
                              <span><strong>{app.years_experience}</strong> years exp</span>
                              <span><strong>${app.hourly_rate}</strong>/hour</span>
                              <span><strong>{app.availability_hours}</strong> hrs/week</span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {app.expertise.slice(0, 4).map((exp, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">{exp}</Badge>
                              ))}
                              {app.expertise.length > 4 && (
                                <Badge variant="outline" className="text-xs">+{app.expertise.length - 4} more</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Applied: {new Date(app.created_at).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setSelectedApplication(app)}
                            >
                              <Eye size={14} className="mr-1" /> View
                            </Button>
                            {app.status === 'pending' && (
                              <>
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApproveApplication(app.id)}
                                  disabled={processingApp}
                                >
                                  <CheckCircle size={14} className="mr-1" /> Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => {
                                    setSelectedApplication(app);
                                  }}
                                  disabled={processingApp}
                                >
                                  <XCircle size={14} className="mr-1" /> Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Background Checks Tab */}
        {activeTab === 'background-checks' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {bgCheckStats && (
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Clock className="text-yellow-500" size={24} />
                      <div>
                        <p className="text-2xl font-bold">{bgCheckStats.pending_review}</p>
                        <p className="text-sm text-muted-foreground">Pending Review</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Activity className="text-blue-500" size={24} />
                      <div>
                        <p className="text-2xl font-bold">{bgCheckStats.processing}</p>
                        <p className="text-sm text-muted-foreground">Processing</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-green-500" size={24} />
                      <div>
                        <p className="text-2xl font-bold">{bgCheckStats.completed}</p>
                        <p className="text-sm text-muted-foreground">Completed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Shield className="text-indigo-500" size={24} />
                      <div>
                        <p className="text-2xl font-bold">{bgCheckStats.total}</p>
                        <p className="text-sm text-muted-foreground">Total Checks</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Background Checks List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield size={20} />
                  Background Check Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {backgroundChecks.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No background check requests yet</p>
                ) : (
                  <div className="space-y-4">
                    {backgroundChecks.map((check) => (
                      <div 
                        key={check.id} 
                        className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        data-testid={`bg-check-${check.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">
                                {check.candidate_info?.first_name} {check.candidate_info?.last_name}
                              </h3>
                              <Badge className={
                                check.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                                check.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                check.status === 'completed' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {check.status === 'pending_review' ? 'Pending Review' :
                                 check.status.charAt(0).toUpperCase() + check.status.slice(1)}
                              </Badge>
                            </div>
                            <div className="space-y-1 text-sm text-muted-foreground">
                              <p className="flex items-center gap-2">
                                <Mail size={14} />
                                {check.candidate_info?.email || 'N/A'}
                              </p>
                              <p className="flex items-center gap-2">
                                <MapPin size={14} />
                                {check.candidate_info?.state || 'N/A'}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-4 mt-2 text-sm">
                              <span><strong>Package:</strong> {check.package?.charAt(0).toUpperCase() + check.package?.slice(1)}</span>
                              <span><strong>Amount:</strong> ${check.amount}</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Submitted: {new Date(check.submitted_at || check.created_at).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setSelectedBgCheck(check)}
                            >
                              <Eye size={14} className="mr-1" /> View
                            </Button>
                            {check.status === 'pending_review' && (
                              <Button 
                                size="sm" 
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => handleMarkBgCheckProcessing(check.id)}
                                disabled={processingBgCheck}
                              >
                                <Activity size={14} className="mr-1" /> Mark Processing
                              </Button>
                            )}
                            {check.status === 'processing' && (
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleMarkBgCheckComplete(check.id)}
                                disabled={processingBgCheck}
                              >
                                <CheckCircle size={14} className="mr-1" /> Mark Complete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instructions for Admin */}
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-900">How to Process Background Checks</CardTitle>
              </CardHeader>
              <CardContent className="text-blue-800 space-y-2">
                <p>1. <strong>Review</strong> the candidate information in the pending requests above</p>
                <p>2. <strong>Log into Checkr</strong> dashboard and create a new candidate with the provided information</p>
                <p>3. <strong>Create a report</strong> for the appropriate package (Standard, Professional, or Comprehensive)</p>
                <p>4. <strong>Mark as Processing</strong> once you&apos;ve initiated the check in Checkr</p>
                <p>5. <strong>Mark as Complete</strong> once Checkr confirms the check is done</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Application Detail Modal */}
      <Dialog open={!!selectedApplication} onOpenChange={() => { setSelectedApplication(null); setRejectReason(''); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mentor Application Details</DialogTitle>
          </DialogHeader>
          
          {selectedApplication && (
            <div className="space-y-6">
              {/* Applicant Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {selectedApplication.user_name.charAt(0)}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold">{selectedApplication.user_name}</h3>
                  <p className="text-muted-foreground">{selectedApplication.user_email}</p>
                  <p className="font-medium mt-1">{selectedApplication.current_role} at {selectedApplication.company}</p>
                  <Badge className={
                    selectedApplication.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    selectedApplication.status === 'approved' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }>
                    {selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                  </Badge>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold">{selectedApplication.years_experience}</p>
                  <p className="text-xs text-muted-foreground">Years Experience</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold">${selectedApplication.hourly_rate}</p>
                  <p className="text-xs text-muted-foreground">Hourly Rate</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold">{selectedApplication.availability_hours}</p>
                  <p className="text-xs text-muted-foreground">Hours/Week</p>
                </div>
              </div>

              {/* Expertise */}
              <div>
                <h4 className="font-semibold mb-2">Areas of Expertise</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedApplication.expertise.map((exp, idx) => (
                    <Badge key={idx} variant="secondary">{exp}</Badge>
                  ))}
                </div>
              </div>

              {/* Specializations */}
              {selectedApplication.specializations?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Specializations</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedApplication.specializations.map((spec, idx) => (
                      <Badge key={idx} variant="outline">{spec}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Bio */}
              <div>
                <h4 className="font-semibold mb-2">Professional Bio</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                  {selectedApplication.bio}
                </p>
              </div>

              {/* Motivation */}
              <div>
                <h4 className="font-semibold mb-2">Why They Want to Mentor</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                  {selectedApplication.motivation}
                </p>
              </div>

              {/* Links */}
              <div className="flex gap-4">
                {selectedApplication.linkedin_url && (
                  <a 
                    href={selectedApplication.linkedin_url.startsWith('http') ? selectedApplication.linkedin_url : `https://${selectedApplication.linkedin_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                  >
                    View LinkedIn →
                  </a>
                )}
                {selectedApplication.portfolio_url && (
                  <a 
                    href={selectedApplication.portfolio_url.startsWith('http') ? selectedApplication.portfolio_url : `https://${selectedApplication.portfolio_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                  >
                    View Portfolio →
                  </a>
                )}
              </div>

              {/* Actions for pending applications */}
              {selectedApplication.status === 'pending' && (
                <div className="border-t pt-4 space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Rejection Reason (optional)</h4>
                    <Textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Provide feedback for the applicant..."
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleApproveApplication(selectedApplication.id)}
                      disabled={processingApp}
                    >
                      <CheckCircle size={16} className="mr-2" /> Approve Application
                    </Button>
                    <Button 
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleRejectApplication(selectedApplication.id)}
                      disabled={processingApp}
                    >
                      <XCircle size={16} className="mr-2" /> Reject Application
                    </Button>
                  </div>
                </div>
              )}

              {/* Show admin notes for rejected applications */}
              {selectedApplication.status === 'rejected' && selectedApplication.admin_notes && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-2">Rejection Reason</h4>
                  <p className="text-sm text-red-700">{selectedApplication.admin_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Background Check Detail Modal */}
      <Dialog open={!!selectedBgCheck} onOpenChange={() => setSelectedBgCheck(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="text-blue-600" size={20} />
              Background Check Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedBgCheck && (
            <div className="space-y-4">
              {/* Candidate Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Candidate Information</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">First Name:</span>
                    <p className="font-medium">{selectedBgCheck.candidate_info?.first_name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Name:</span>
                    <p className="font-medium">{selectedBgCheck.candidate_info?.last_name || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Email:</span>
                    <p className="font-medium">{selectedBgCheck.candidate_info?.email || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">State:</span>
                    <p className="font-medium">{selectedBgCheck.candidate_info?.state || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Check Info */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Package:</span>
                  <span className="font-medium">{selectedBgCheck.package?.charAt(0).toUpperCase() + selectedBgCheck.package?.slice(1)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-medium">${selectedBgCheck.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge className={
                    selectedBgCheck.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                    selectedBgCheck.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                    selectedBgCheck.status === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {selectedBgCheck.status === 'pending_review' ? 'Pending Review' :
                     selectedBgCheck.status.charAt(0).toUpperCase() + selectedBgCheck.status.slice(1)}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Submitted:</span>
                  <span>{new Date(selectedBgCheck.submitted_at || selectedBgCheck.created_at).toLocaleDateString()}</span>
                </div>
                {selectedBgCheck.shareable_link && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Share Link:</span>
                    <span className="font-mono text-xs">{selectedBgCheck.shareable_link}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              {selectedBgCheck.status === 'pending_review' && (
                <div className="pt-4 border-t">
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleMarkBgCheckProcessing(selectedBgCheck.id)}
                    disabled={processingBgCheck}
                  >
                    <Activity size={16} className="mr-2" />
                    Mark as Processing in Checkr
                  </Button>
                </div>
              )}
              {selectedBgCheck.status === 'processing' && (
                <div className="pt-4 border-t">
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => handleMarkBgCheckComplete(selectedBgCheck.id)}
                    disabled={processingBgCheck}
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Mark as Completed
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
